import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import SideNavTeamsSection from "./sections/SideNavTeamsSection";
import SideNavPinnedSection from "./sections/SideNavPinnedSection";

// Hooks
import useSideNavState from "./hooks/useSideNavState";
import useResponsive from "./hooks/useResponsive";
import useResize from "./hooks/useResize";

// Utilities
import { categorizeItems } from "./utils/itemUtils";

// Styled Components
import {
  SideNavWrapper,
  SideNavItems,
  ToggleHandle,
  ToggleIcon,
} from "./styles/SideNavV2.styles";

// Components
import ExpandedPanel from "./components/ExpandedPanel";
import ItemSection from "./components/ItemSection";
import CurrentViewSectionPortalTarget from "./components/CurrentViewSectionPortalTarget";
import Icon from "../Icon";

/**
 * SideNavV2 - A responsive side navigation component with expandable panels
 *
 * Features:
 * - Responsive design that adapts to mobile and desktop
 * - Expandable panels with resizable functionality (desktop: width, mobile: height)
 * - Toggle handle to expand/collapse the sidebar (desktop only)
 * - Team and pinned item sections (optional custom render props)
 * - Current view section portal integration
 *
 * @param {Object} props - Component props
 * @param {Array} props.items - Navigation items to display
 * @param {string} props.sideNavHeight - Height of the side navigation
 * @param {Array} [props.yourTeams] - Teams to display in the teams section
 * @param {Array} [props.pinnedItems] - Pinned items to display
 * @param {boolean} [props.startExpanded] - Whether the sidebar should start expanded
 * @param {(keepExpanded: boolean) => void} [props.onKeepExpandedChange] - Callback when the sidebar expand state changes (e.g. after toggle)
 * @param {Function} [props.renderTeamsSection] - Custom render function for teams section; receives { teams, isExpanded }
 * @param {Function} [props.renderPinnedSection] - Custom render function for pinned section; receives { items, isExpanded }
 * @returns {JSX.Element} Rendered side navigation
 */
const SideNavV2 = ({
  items,
  sideNavHeight,
  yourTeams,
  pinnedItems,
  startExpanded,
  onKeepExpandedChange,
  renderTeamsSection,
  renderPinnedSection,
}) => {
  const {
    expandedItem,
    isExpanded,
    expandedWidth,
    expandedRef,
    navItemRefs,
    wrapperRef,
    handleItemClick,
    handleWidthChange,
    handleExpandToggle: handleExpandToggleState,
  } = useSideNavState(items, startExpanded);

  const { isSmallScreen } = useResponsive();

  const handleExpandToggle = useCallback(() => {
    handleExpandToggleState();
    // notify consumer of expanded state change
    const newExpandedState = !isExpanded;
    onKeepExpandedChange?.(newExpandedState);
  }, [isExpanded, onKeepExpandedChange, handleExpandToggleState]);

  // Memoize expensive calculations to prevent unnecessary re-renders
  const currentItem = useMemo(
    () => (expandedItem !== null ? items[expandedItem] : null),
    [expandedItem, items]
  );

  const { isResizing, hasResized, handleResizeStart } = useResize(
    expandedRef,
    isSmallScreen,
    expandedItem,
    handleWidthChange,
    currentItem
  );

  // Memoize categorized items to prevent recalculation on every render
  const categorizedItems = useMemo(() => categorizeItems(items), [items]);
  const { topAlignedItems, pageSpecificItems, bottomAlignedItems, allItems } =
    categorizedItems;

  const toggleIcon = useMemo(
    () => (isExpanded ? ["far", "chevron-left"] : ["far", "chevron-right"]),
    [isExpanded]
  );

  return (
    <SideNavWrapper
      ref={wrapperRef}
      sideNavHeight={sideNavHeight}
      data-testid="side-nav-wrapper"
    >
      <SideNavItems isExpanded={isExpanded} data-testid="side-nav-items">
        <ItemSection
          items={topAlignedItems}
          isExpanded={isExpanded}
          isSmallScreen={isSmallScreen}
          expandedItem={expandedItem}
          handleItemClick={handleItemClick}
          navItemRefs={navItemRefs}
        />

        <ItemSection
          items={pageSpecificItems}
          isExpanded={isExpanded}
          isSmallScreen={isSmallScreen}
          expandedItem={expandedItem}
          handleItemClick={handleItemClick}
          navItemRefs={navItemRefs}
        />

        <CurrentViewSectionPortalTarget />

        {yourTeams &&
          yourTeams.length > 0 &&
          (renderTeamsSection ? (
            renderTeamsSection({ teams: yourTeams, isExpanded })
          ) : (
            <SideNavTeamsSection teams={yourTeams} isExpanded={isExpanded} />
          ))}

        {pinnedItems &&
          pinnedItems.length > 0 &&
          (renderPinnedSection ? (
            renderPinnedSection({ items: pinnedItems, isExpanded })
          ) : (
            <SideNavPinnedSection items={pinnedItems} isExpanded={isExpanded} />
          ))}

        <ItemSection
          items={bottomAlignedItems}
          isExpanded={isExpanded}
          isSmallScreen={isSmallScreen}
          expandedItem={expandedItem}
          handleItemClick={handleItemClick}
          navItemRefs={navItemRefs}
        />
      </SideNavItems>

      {!isSmallScreen && (
        <ToggleHandle
          type="button"
          aria-label={
            isExpanded ? "Collapse side navigation" : "Expand side navigation"
          }
          aria-expanded={isExpanded}
          onClick={handleExpandToggle}
          data-testid="toggle-handle"
          isExpanded={isExpanded}
        >
          <ToggleIcon className="toggle-icon">
            <Icon icon={toggleIcon} size="sm" fontSize="15px" />
          </ToggleIcon>
        </ToggleHandle>
      )}

      {allItems.map((item, index) => {
        if (item.actionType !== "component") return null;
        if (index !== expandedItem || item.hide) return null;

        return (
          <ExpandedPanel
            key={item.name}
            item={item}
            expandedItem={expandedItem}
            isExpanded={isExpanded}
            expandedWidth={expandedWidth}
            isSmallScreen={isSmallScreen}
            expandedRef={expandedRef}
            onResizeStart={handleResizeStart}
            onItemClick={handleItemClick}
            isResizing={isResizing}
            hasResized={hasResized}
          />
        );
      })}
    </SideNavWrapper>
  );
};

SideNavV2.propTypes = {
  sideNavHeight: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      iconName: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      badgeNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      badgeDot: PropTypes.bool,
      hide: PropTypes.bool,
      large: PropTypes.bool,
      bottomAligned: PropTypes.bool,
      pageSpecific: PropTypes.bool,
      isExpandedByDefault: PropTypes.bool,
      isActive: PropTypes.bool,
      actionType: PropTypes.oneOf(["component", "link", "button"]).isRequired,
      component: PropTypes.elementType,
      link: PropTypes.string,
      onClick: PropTypes.func,
      index: PropTypes.number,
    })
  ).isRequired,
  yourTeams: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string,
      name: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired,
    })
  ),
  pinnedItems: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string,
      name: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired,
      shape: PropTypes.string,
      onUnpin: PropTypes.func,
    })
  ),
  startExpanded: PropTypes.bool,
  onKeepExpandedChange: PropTypes.func,
  renderTeamsSection: PropTypes.func,
  renderPinnedSection: PropTypes.func,
};

export default SideNavV2;
